import dayjs from "dayjs";
import { formatChartTooltipValue } from "./formatters";

const LINE_PALETTE = [
    "#1890ff",
    "#52c41a",
    "#722ed1",
    "#fa8c16",
    "#13c2c2",
    "#eb2f96",
    "#2f54eb",
    "#a0d911",
];

/**
 * @param {{ combined: { date: string }[] }[]} seriesList
 * @returns {string[]}
 */
export function unionSortedDatesFromCombined(seriesList) {
    const s = new Set();
    (seriesList || []).forEach(({ combined }) => {
        (combined || []).forEach((p) => {
            if (p?.date) s.add(p.date);
        });
    });
    return Array.from(s).sort();
}

/**
 * @param {{ volumeTrend: { date: string }[] }[]} seriesList
 * @returns {string[]}
 */
export function unionSortedDatesFromVolumeTrend(seriesList) {
    const s = new Set();
    (seriesList || []).forEach(({ volumeTrend }) => {
        (volumeTrend || []).forEach((p) => {
            if (p?.date) s.add(p.date);
        });
    });
    return Array.from(s).sort();
}

/**
 * 多对冲组「每日总成交量」（两腿合计）对比折线；无成交日为 0
 */
export function buildMultiGroupVolumeTrendOption({ seriesList, isDark }) {
    const datesRaw = unionSortedDatesFromVolumeTrend(seriesList || []);
    if (datesRaw.length === 0) {
        return null;
    }

    const xLabels = datesRaw.map((d) => dayjs(d).format("MM-DD"));
    const textColor = isDark ? "rgba(255, 255, 255, 0.45)" : "#64748b";
    const splitLineColor = isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)";
    const axisLineColor = isDark ? "rgba(255, 255, 255, 0.15)" : "#e2e8f0";

    const echartsSeries = (seriesList || []).map((item, idx) => {
        const map = new Map((item.volumeTrend || []).map((p) => [p.date, p.total_volume ?? p.totalVolume ?? 0]));
        const data = datesRaw.map((d) => (map.has(d) ? map.get(d) : 0));
        const color = LINE_PALETTE[idx % LINE_PALETTE.length];
        return {
            name: item.group?.name || `组${idx + 1}`,
            type: "line",
            data,
            smooth: true,
            lineStyle: { width: 2.5, color },
            itemStyle: { color },
            symbol: "circle",
            symbolSize: datesRaw.length <= 20 ? 5 : 3,
            emphasis: { focus: "series" },
        };
    });

    const hasAnyPoint = echartsSeries.some((ser) => ser.data.some((v) => v > 0));
    if (!hasAnyPoint) {
        return null;
    }

    return {
        backgroundColor: "transparent",
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "cross" },
            backgroundColor: isDark ? "rgba(30, 35, 45, 0.95)" : "rgba(255, 255, 255, 0.95)",
            borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
            textStyle: { color: isDark ? "#f0f0f5" : "#1f2937" },
            formatter: (params) => {
                if (!params?.length) return "";
                let html = `<div style="margin-bottom:4px;font-weight:600">${params[0].axisValue}</div>`;
                params.forEach((item) => {
                    const shown = formatChartTooltipValue(item.value);
                    html += `<div style="display:flex;justify-content:space-between;gap:16px;"><span>${item.marker} ${item.seriesName}</span><b>${shown}</b></div>`;
                });
                return html;
            },
        },
        legend: {
            type: "scroll",
            bottom: 0,
            data: echartsSeries.map((s) => s.name),
            textStyle: { color: textColor, fontSize: 11 },
        },
        grid: { left: "3%", right: "4%", bottom: "20%", top: "12%", containLabel: true },
        xAxis: {
            type: "category",
            data: xLabels,
            axisLabel: { color: textColor },
            axisLine: { lineStyle: { color: axisLineColor } },
        },
        yAxis: [
            {
                type: "value",
                name: "日成交量",
                nameTextStyle: { color: textColor },
                scale: true,
                axisLabel: { color: textColor },
                splitLine: { lineStyle: { type: "dashed", color: splitLineColor } },
            },
        ],
        series: echartsSeries,
    };
}

/**
 * 多对冲组「组合净资产」对比（统一时间轴，缺失日 connectNulls）
 */
export function buildMultiGroupNetAssetsOption({ seriesList, isDark }) {
    const withData = (seriesList || []).filter((s) => (s.combined || []).length > 0);
    const datesRaw = unionSortedDatesFromCombined(withData);
    if (datesRaw.length === 0) {
        return null;
    }

    const xLabels = datesRaw.map((d) => dayjs(d).format("MM-DD"));
    const textColor = isDark ? "rgba(255, 255, 255, 0.45)" : "#64748b";
    const splitLineColor = isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)";
    const axisLineColor = isDark ? "rgba(255, 255, 255, 0.15)" : "#e2e8f0";

    const echartsSeries = withData.map((item, idx) => {
        const map = new Map((item.combined || []).map((p) => [p.date, p.net_assets]));
        const data = datesRaw.map((d) => (map.has(d) ? map.get(d) : null));
        const color = LINE_PALETTE[idx % LINE_PALETTE.length];
        return {
            name: item.group?.name || `组${idx + 1}`,
            type: "line",
            data,
            smooth: true,
            connectNulls: true,
            lineStyle: { width: 2.5, color },
            itemStyle: { color },
            symbol: "circle",
            symbolSize: datesRaw.length <= 20 ? 5 : 3,
            emphasis: { focus: "series" },
        };
    });

    return {
        backgroundColor: "transparent",
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "cross" },
            backgroundColor: isDark ? "rgba(30, 35, 45, 0.95)" : "rgba(255, 255, 255, 0.95)",
            borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
            textStyle: { color: isDark ? "#f0f0f5" : "#1f2937" },
            formatter: (params) => {
                if (!params?.length) return "";
                let html = `<div style="margin-bottom:4px;font-weight:600">${params[0].axisValue}</div>`;
                params.forEach((item) => {
                    const shown = formatChartTooltipValue(item.value);
                    html += `<div style="display:flex;justify-content:space-between;gap:16px;"><span>${item.marker} ${item.seriesName}</span><b>${shown}</b></div>`;
                });
                return html;
            },
        },
        legend: {
            type: "scroll",
            bottom: 0,
            data: echartsSeries.map((s) => s.name),
            textStyle: { color: textColor, fontSize: 11 },
        },
        grid: { left: "3%", right: "4%", bottom: "20%", top: "12%", containLabel: true },
        xAxis: {
            type: "category",
            data: xLabels,
            axisLabel: { color: textColor },
            axisLine: { lineStyle: { color: axisLineColor } },
        },
        yAxis: [
            {
                type: "value",
                name: "组合净资产",
                nameTextStyle: { color: textColor },
                scale: true,
                axisLabel: { color: textColor },
                splitLine: { lineStyle: { type: "dashed", color: splitLineColor } },
            },
        ],
        series: echartsSeries,
    };
}

/**
 * 各组区间内总交易量（两成员成交量之和，与 trade_volumes 一致）
 */
export function buildGroupPeriodVolumeBarOption({ seriesList, isDark }) {
    const rows = (seriesList || []).map((s) => ({
        name: s.group?.name || "—",
        value: s.analysis?.period_total_volume ?? 0,
    }));
    if (!rows.length) return null;

    rows.sort((a, b) => b.value - a.value);

    const textColor = isDark ? "rgba(255, 255, 255, 0.45)" : "#64748b";
    const splitLineColor = isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)";

    return {
        backgroundColor: "transparent",
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
            backgroundColor: isDark ? "rgba(30, 35, 45, 0.95)" : "rgba(255, 255, 255, 0.95)",
            borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
            textStyle: { color: isDark ? "#f0f0f5" : "#1f2937" },
            formatter: (params) => {
                const p = params[0];
                return `${p.name}<br/>区间总交易量：<b>${formatChartTooltipValue(p.value)}</b>`;
            },
        },
        grid: { left: "12%", right: "8%", top: "8%", bottom: "8%", containLabel: true },
        xAxis: {
            type: "value",
            name: "成交量",
            nameTextStyle: { color: textColor },
            axisLabel: { color: textColor },
            splitLine: { lineStyle: { color: splitLineColor } },
        },
        yAxis: {
            type: "category",
            data: rows.map((r) => r.name),
            axisLabel: { color: textColor, width: 100, overflow: "truncate" },
            axisLine: { lineStyle: { color: isDark ? "rgba(255,255,255,0.15)" : "#e2e8f0" } },
        },
        series: [
            {
                type: "bar",
                data: rows.map((r) => ({
                    value: r.value,
                    itemStyle: {
                        color: "#1677ff",
                        borderRadius: [0, 4, 4, 0],
                    },
                })),
                barMaxWidth: 22,
                label: {
                    show: true,
                    position: "right",
                    color: textColor,
                    fontSize: 10,
                    formatter: (p) => {
                        const v = Number(p.value);
                        if (v === 0) return "0";
                        return Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0);
                    },
                },
            },
        ],
    };
}

/**
 * 各组区间累计组合盈亏（横向条形，便于长名称）
 */
export function buildGroupPeriodPnLBarOption({ seriesList, isDark }) {
    const rows = (seriesList || []).map((s) => ({
        name: s.group?.name || "—",
        value: s.analysis?.total_pnl ?? 0,
    }));
    if (!rows.length) return null;

    rows.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

    const textColor = isDark ? "rgba(255, 255, 255, 0.45)" : "#64748b";
    const splitLineColor = isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)";

    return {
        backgroundColor: "transparent",
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
            backgroundColor: isDark ? "rgba(30, 35, 45, 0.95)" : "rgba(255, 255, 255, 0.95)",
            borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
            textStyle: { color: isDark ? "#f0f0f5" : "#1f2937" },
            formatter: (params) => {
                const p = params[0];
                const v = Number(p.value);
                const colored = `<span style="color:${v >= 0 ? "#52c41a" : "#f5222d"};font-weight:600">${formatChartTooltipValue(v)} USDT</span>`;
                return `${p.name}<br/>区间累计盈亏：${colored}`;
            },
        },
        grid: { left: "12%", right: "8%", top: "8%", bottom: "8%", containLabel: true },
        xAxis: {
            type: "value",
            name: "USDT",
            nameTextStyle: { color: textColor },
            axisLabel: { color: textColor },
            splitLine: { lineStyle: { color: splitLineColor } },
        },
        yAxis: {
            type: "category",
            data: rows.map((r) => r.name),
            axisLabel: { color: textColor, width: 100, overflow: "truncate" },
            axisLine: { lineStyle: { color: isDark ? "rgba(255,255,255,0.15)" : "#e2e8f0" } },
        },
        series: [
            {
                type: "bar",
                data: rows.map((r) => ({
                    value: r.value,
                    itemStyle: {
                        color: r.value >= 0 ? "#52c41a" : "#f5222d",
                        borderRadius: [0, 4, 4, 0],
                    },
                })),
                barMaxWidth: 22,
                label: {
                    show: true,
                    position: "right",
                    color: textColor,
                    fontSize: 10,
                    formatter: (p) => {
                        const v = p.value;
                        return Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0);
                    },
                },
            },
        ],
    };
}

/**
 * 各组平均对冲效率（竖条，便于与盈亏并排展示）
 */
export function buildGroupHedgeEfficiencyBarOption({ seriesList, isDark }) {
    const rows = (seriesList || []).map((s) => ({
        name: s.group?.name || "—",
        value: s.analysis?.avg_hedge_efficiency ?? 0,
    }));
    if (!rows.length) return null;

    const textColor = isDark ? "rgba(255, 255, 255, 0.45)" : "#64748b";
    const splitLineColor = isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)";

    const barColor = (v) => {
        if (v >= 70) return "#52c41a";
        if (v >= 40) return "#faad14";
        return "#f5222d";
    };

    return {
        backgroundColor: "transparent",
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
            backgroundColor: isDark ? "rgba(30, 35, 45, 0.95)" : "rgba(255, 255, 255, 0.95)",
            borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
            textStyle: { color: isDark ? "#f0f0f5" : "#1f2937" },
            formatter: (params) => {
                const p = params[0];
                return `${p.name}<br/>平均对冲效率：<b>${formatChartTooltipValue(p.value)}%</b>`;
            },
        },
        grid: { left: "3%", right: "4%", bottom: "22%", top: "12%", containLabel: true },
        xAxis: {
            type: "category",
            data: rows.map((r) => r.name),
            axisLabel: { color: textColor, rotate: rows.length > 5 ? 28 : 0, fontSize: 10 },
            axisLine: { lineStyle: { color: isDark ? "rgba(255,255,255,0.15)" : "#e2e8f0" } },
        },
        yAxis: {
            type: "value",
            name: "%",
            max: 100,
            nameTextStyle: { color: textColor },
            axisLabel: { color: textColor },
            splitLine: { lineStyle: { color: splitLineColor } },
        },
        series: [
            {
                type: "bar",
                data: rows.map((r) => ({
                    value: r.value,
                    itemStyle: { color: barColor(r.value), borderRadius: [4, 4, 0, 0] },
                })),
                barMaxWidth: 36,
                label: {
                    show: true,
                    position: "top",
                    color: textColor,
                    fontSize: 10,
                    formatter: (p) => `${Number(p.value).toFixed(0)}%`,
                },
            },
        ],
    };
}
